import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Clock,
  Wrench,
  Download,
  DollarSign,
  Calendar,
  CheckCircle2,
  Plus,
  FileText,
  AlertTriangle,
  MessageSquare
} from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/*
 * Altam™ Operating Services
 * Employee Portal Dashboard
 * Copyright (c) 2026 Pecaro Consulting Inc. All rights reserved.
 */

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalHours: 0,
    pendingTimeEntries: 0,
    assignedMaintenance: 0,
    latestPaystub: null
  });
  const [recentTimeEntries, setRecentTimeEntries] = useState([]);
  const [assignedRequests, setAssignedRequests] = useState([]);
  const [payrolls, setPayrolls] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [timeEntriesRes, maintenanceRes, payrollRes] = await Promise.all([
        axios.get(`${API}/time-entries`),
        axios.get(`${API}/maintenance`),
        axios.get(`${API}/payroll`)
      ]);

      const timeEntries = timeEntriesRes.data;
      const maintenance = maintenanceRes.data;
      const payrollData = payrollRes.data;

      // Calculate stats
      const thisMonthEntries = timeEntries.filter(e => {
        const entryDate = new Date(e.date);
        const now = new Date();
        return entryDate.getMonth() === now.getMonth() && entryDate.getFullYear() === now.getFullYear();
      });

      setStats({
        totalHours: thisMonthEntries.reduce((sum, e) => sum + e.hours, 0),
        pendingTimeEntries: timeEntries.filter(e => e.status === 'pending').length,
        assignedMaintenance: maintenance.filter(m => m.status !== 'completed').length,
        latestPaystub: payrollData[0] || null
      });

      setRecentTimeEntries(timeEntries.slice(0, 5));
      setAssignedRequests(maintenance.filter(m => m.status !== 'completed').slice(0, 5));
      setPayrolls(payrollData.slice(0, 3));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadPaystub = async (payrollId) => {
    try {
      const response = await axios.get(`${API}/payroll/${payrollId}/paystub`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `paystub_${payrollId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to download paystub:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2 }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="employee-dashboard">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-violet-600 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold">Welcome, {user?.first_name || 'Team Member'}!</h1>
        <p className="text-indigo-100 mt-1">
          Track your time, view assignments, and access your pay information.
        </p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Link
          to="/time-entries"
          className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md hover:border-indigo-300 transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="bg-indigo-100 p-3 rounded-xl group-hover:bg-indigo-200 transition-colors">
              <Clock className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Log Time</h3>
              <p className="text-sm text-slate-500">Submit timesheet entry</p>
            </div>
          </div>
        </Link>

        <Link
          to="/maintenance"
          className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md hover:border-indigo-300 transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="bg-amber-100 p-3 rounded-xl group-hover:bg-amber-200 transition-colors">
              <Wrench className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Work Orders</h3>
              <p className="text-sm text-slate-500">View maintenance tasks</p>
            </div>
          </div>
        </Link>

        <Link
          to="/communications"
          className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md hover:border-indigo-300 transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="bg-violet-100 p-3 rounded-xl group-hover:bg-violet-200 transition-colors">
              <MessageSquare className="w-6 h-6 text-violet-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Messages</h3>
              <p className="text-sm text-slate-500">Communication center</p>
            </div>
          </div>
        </Link>

        <Link
          to="/payroll"
          className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md hover:border-indigo-300 transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="bg-emerald-100 p-3 rounded-xl group-hover:bg-emerald-200 transition-colors">
              <DollarSign className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Pay Stubs</h3>
              <p className="text-sm text-slate-500">View and download</p>
            </div>
          </div>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="bg-indigo-100 p-2 rounded-lg">
              <Clock className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-indigo-600">{stats.totalHours.toFixed(1)}</p>
              <p className="text-sm text-slate-500">Hours This Month</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="bg-amber-100 p-2 rounded-lg">
              <Calendar className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600">{stats.pendingTimeEntries}</p>
              <p className="text-sm text-slate-500">Pending Entries</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="bg-rose-100 p-2 rounded-lg">
              <Wrench className="w-5 h-5 text-rose-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-rose-600">{stats.assignedMaintenance}</p>
              <p className="text-sm text-slate-500">Active Work Orders</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="bg-emerald-100 p-2 rounded-lg">
              <DollarSign className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-600">
                {stats.latestPaystub ? formatCurrency(stats.latestPaystub.net_pay) : '$0'}
              </p>
              <p className="text-sm text-slate-500">Last Pay</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Time Entries */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Recent Time Entries</h3>
            <Link to="/time-entries" className="text-sm text-indigo-600 hover:text-indigo-700">
              View All →
            </Link>
          </div>
          <div className="space-y-3">
            {recentTimeEntries.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-slate-500">
                <Clock className="w-5 h-5 mr-2 text-slate-300" />
                No time entries yet
              </div>
            ) : (
              recentTimeEntries.map((entry) => (
                <div key={entry.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="bg-slate-100 p-2 rounded-lg">
                      <Clock className="w-4 h-4 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{entry.category}</p>
                      <p className="text-xs text-slate-500">{entry.date} • {entry.hours}h</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    entry.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {entry.status}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Assigned Work Orders */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Active Work Orders</h3>
            <Link to="/maintenance" className="text-sm text-indigo-600 hover:text-indigo-700">
              View All →
            </Link>
          </div>
          <div className="space-y-3">
            {assignedRequests.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-slate-500">
                <CheckCircle2 className="w-5 h-5 mr-2 text-emerald-500" />
                No active work orders
              </div>
            ) : (
              assignedRequests.map((req) => (
                <div key={req.id} className="flex items-start justify-between py-2 border-b border-slate-100 last:border-0">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${
                      req.priority === 'high' ? 'bg-rose-100' : 
                      req.priority === 'medium' ? 'bg-amber-100' : 'bg-slate-100'
                    }`}>
                      <Wrench className={`w-4 h-4 ${
                        req.priority === 'high' ? 'text-rose-600' : 
                        req.priority === 'medium' ? 'text-amber-600' : 'text-slate-600'
                      }`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{req.title}</p>
                      <p className="text-xs text-slate-500">{req.unit_number} • {req.entity_name}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full capitalize ${
                    req.priority === 'high' ? 'bg-rose-100 text-rose-700' : 
                    req.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    {req.priority}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Recent Pay Stubs */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">Recent Pay Stubs</h3>
          <Link to="/payroll" className="text-sm text-indigo-600 hover:text-indigo-700">
            View All →
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Pay Period</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Hours</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Gross</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Deductions</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Net Pay</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Download</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {payrolls.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-slate-500">
                    No pay stubs available
                  </td>
                </tr>
              ) : (
                payrolls.map((p) => (
                  <tr key={p.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-900">{p.pay_period_start} - {p.pay_period_end}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 text-right">{p.hours_worked}</td>
                    <td className="px-4 py-3 text-right font-medium text-slate-900">{formatCurrency(p.gross_pay)}</td>
                    <td className="px-4 py-3 text-right text-sm text-rose-600">
                      {formatCurrency(p.cpp_deduction + p.ei_deduction + p.federal_tax + p.provincial_tax)}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-emerald-600">{formatCurrency(p.net_pay)}</td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => downloadPaystub(p.id)}
                        className="text-indigo-600 hover:text-indigo-700 p-1"
                        data-testid={`download-paystub-${p.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
